import { useEffect, useMemo, useRef, useState } from 'react'
import {
    DRIFTEVENTSTART,
    DRIFTEVENTSTOP,
    DRIFTEVENTUPDATE,
    DRIFT_HUD_DEBUG_CLEAR_EVENT,
    DRIFT_HUD_DEBUG_FEED_EVENT,
    DriftAchievementGrant,
    DriftDto,
    DriftHudState,
    ResolvedHudFrame,
    ShortDriftAchievementType,
} from '../pages/hud/drift/DriftHud.types'

interface UseDriftHudStateProps {
    driftData: DriftDto | null
    unsuccessedText: string
}

const ZEROSCOREHIDEDELAY = 2000
const FINALSCOREHIDEDELAY = 3500
const FOOTERHIDEDELAY = 500

const initialState: DriftHudState = {
    isShown: false,
    score: 0,
    scoreToShow: 0,
    footerAnimation: '',
    isFinalVisible: false,
    finalScore: 0,
    playMultiplierShine: false,
    achievementTitle: '',
    achievementPoints: 0,
    totalScore: 0,
    multiplier: 1.0,
    isPairActive: false,
    pairBonusScore: 0,
    pairStreakSec: 0,
}

const toSafeNumber = (value: unknown, fallback = 0): number => {
    const num = Number(value)
    return Number.isFinite(num) ? num : fallback
}

const normalizeAchievementTitle = (
    type: ShortDriftAchievementType | null | undefined,
    grade?: number | null,
): string => {
    if (!type) {
        return ''
    }

    const baseTitle = (() => {
        switch (type) {
            case ShortDriftAchievementType.DRIFT:
                return 'drift'
            case ShortDriftAchievementType.TIRE_WARMUP:
                return 'tire_warmup'
            case ShortDriftAchievementType.HANDBRAKE:
                return 'handbrake_drift'
            case ShortDriftAchievementType.TURN_180:
                return 'turn_180'
            default:
                return String(type).replace(/_/g, ' ')
        }
    })()

    if (!grade || grade <= 0 || type === ShortDriftAchievementType.TURN_180) {
        return baseTitle
    }

    return `${baseTitle} ${grade}`
}

const getLatestNewGrant = (
    current: DriftDto,
    previous: DriftDto | null,
): DriftAchievementGrant | null => {
    const currentAchievements = Array.isArray(current.achievements) ? current.achievements : []
    if (currentAchievements.length === 0) {
        return null
    }

    const previousAchievements = Array.isArray(previous?.achievements) ? previous!.achievements : []
    const previousLength = previousAchievements.length

    if (currentAchievements.length > previousLength) {
        return currentAchievements[currentAchievements.length - 1]
    }

    const currentLast = currentAchievements[currentAchievements.length - 1]
    const previousLast = previousAchievements[previousAchievements.length - 1]

    if (!previousLast) {
        return currentLast
    }

    if (
        currentLast.grantedAtMs !== previousLast.grantedAtMs ||
        currentLast.type !== previousLast.type ||
        currentLast.grade !== previousLast.grade ||
        currentLast.totalPoints !== previousLast.totalPoints
    ) {
        return currentLast
    }

    return null
}

const resolveAchievementPresentation = (
    current: DriftDto,
    previous: DriftDto | null,
): Pick<ResolvedHudFrame, 'achievementTitle' | 'achievementPoints'> => {
    const latestGrant = getLatestNewGrant(current, previous)

    if (latestGrant) {
        return {
            achievementTitle: normalizeAchievementTitle(latestGrant.type, latestGrant.grade),
            achievementPoints: Math.max(0, toSafeNumber(latestGrant.totalPoints)),
        }
    }

    return {
        achievementTitle: normalizeAchievementTitle(
            current.currentAchievementType,
            current.currentAchievementGrade,
        ),
        achievementPoints: 0,
    }
}

const isSameSession = (left: DriftDto | null, right: DriftDto | null): boolean => {
    if (!left || !right) {
        return false
    }

    return String(left.sessionId) === String(right.sessionId)
}

const resolveHudFrame = (
    current: DriftDto | null,
    previous: DriftDto | null,
): ResolvedHudFrame | null => {
    if (!current) {
        return null
    }

    const started =
        current.active &&
        (!previous || !previous.active || !isSameSession(current, previous))

    const updated =
        current.active &&
        !!previous &&
        previous.active &&
        isSameSession(current, previous)

    const stopped =
        !current.active &&
        !!previous &&
        previous.active &&
        isSameSession(current, previous)

    if (!started && !updated && !stopped) {
        return null
    }

    const achievementPresentation = resolveAchievementPresentation(current, previous)
    const driftScore = Math.max(0, Math.round(toSafeNumber(current.trickBaseScore)))
    const totalScore = Math.max(0, Math.round(toSafeNumber(current.totalScore)))
    const multiplier = Math.max(1, toSafeNumber(current.multiplier, 1))
    const isPairActive = !!current.pairActive
    const pairBonusScore = Math.max(0, toSafeNumber(current.pairBonusScore))
    const pairStreakSec = Math.round(toSafeNumber(current.pairBestStreakMs) / 1000)

    if (started) {
        return {
            driftEventType: DRIFTEVENTSTART,
            driftScore,
            achievementTitle: achievementPresentation.achievementTitle,
            achievementPoints: achievementPresentation.achievementPoints,
            multiplier,
            totalScore,
            achievementFinalScore: 0,
            isPairActive,
            pairBonusScore,
            pairStreakSec,
        }
    }

    if (updated) {
        return {
            driftEventType: DRIFTEVENTUPDATE,
            driftScore,
            achievementTitle: achievementPresentation.achievementTitle,
            achievementPoints: achievementPresentation.achievementPoints,
            multiplier,
            totalScore,
            achievementFinalScore: 0,
            isPairActive,
            pairBonusScore,
            pairStreakSec,
        }
    }

    const finalExtraScore = Math.max(0, totalScore - driftScore)

    return {
        driftEventType: DRIFTEVENTSTOP,
        driftScore,
        achievementTitle: achievementPresentation.achievementTitle,
        achievementPoints: achievementPresentation.achievementPoints,
        multiplier,
        totalScore,
        achievementFinalScore: finalExtraScore,
        isPairActive,
        pairBonusScore,
        pairStreakSec,
    }
}

export const useDriftHudState = ({
    driftData,
    unsuccessedText,
}: UseDriftHudStateProps) => {
    const [state, setState] = useState<DriftHudState>(initialState)
    const [debugDriftData, setDebugDriftData] = useState<DriftDto | null>(null)

    const sessionActiveRef = useRef(false)
    const scoreRef = useRef(0)
    const totalScoreRef = useRef(0)
    const multiplierRef = useRef(1.0)
    const achievementTitleRef = useRef('')
    const achievementPointsRef = useRef(0)
    const pairActiveRef = useRef(false)
    const pairBonusRef = useRef(0)

    const previousRawDataRef = useRef<DriftDto | null>(null)

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const finalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const shineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        const handleDebugFeed = (event: Event) => {
            const customEvent = event as CustomEvent<DriftDto | null>
            setDebugDriftData(customEvent.detail ?? null)
        }

        const handleDebugClear = () => {
            setDebugDriftData(null)
            previousRawDataRef.current = null
        }

        window.addEventListener(
            DRIFT_HUD_DEBUG_FEED_EVENT,
            handleDebugFeed as EventListener,
        )
        window.addEventListener(
            DRIFT_HUD_DEBUG_CLEAR_EVENT,
            handleDebugClear as EventListener,
        )

        return () => {
            window.removeEventListener(
                DRIFT_HUD_DEBUG_FEED_EVENT,
                handleDebugFeed as EventListener,
            )
            window.removeEventListener(
                DRIFT_HUD_DEBUG_CLEAR_EVENT,
                handleDebugClear as EventListener,
            )
        }
    }, [])

    const effectiveDriftData = useMemo(
        () => debugDriftData ?? driftData,
        [debugDriftData, driftData],
    )

    const clearTimer = (
        timerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
    ) => {
        if (!timerRef.current) {
            return
        }

        clearTimeout(timerRef.current)
        timerRef.current = null
    }

    const clearHideTimers = () => {
        clearTimer(closeTimerRef)
        clearTimer(hideTimerRef)
    }

    const resetRefs = () => {
        sessionActiveRef.current = false
        scoreRef.current = 0
        totalScoreRef.current = 0
        multiplierRef.current = 1.0
        achievementTitleRef.current = ''
        achievementPointsRef.current = 0
        pairActiveRef.current = false
        pairBonusRef.current = 0
    }

    const resetHud = () => {
        resetRefs()
        setState(initialState)
    }

    const showHud = () => {
        clearHideTimers()

        setState(prev => {
            if (prev.isShown && !prev.footerAnimation) {
                return prev
            }

            return {
                ...prev,
                isShown: true,
                footerAnimation: '',
            }
        })
    }

    const setLiveScore = (nextScore: number) => {
        const stableScore = Math.max(scoreRef.current, Math.round(toSafeNumber(nextScore)))
        scoreRef.current = stableScore

        setState(prev => {
            if (prev.score === stableScore && prev.scoreToShow === stableScore) {
                return prev
            }

            return {
                ...prev,
                score: stableScore,
                scoreToShow: stableScore,
            }
        })
    }

    const setHeaderState = (
        achievementTitle: string,
        achievementPoints: number,
        totalScore: number,
        multiplier: number,
    ) => {
        setState(prev => {
            if (
                prev.achievementTitle === achievementTitle &&
                prev.achievementPoints === achievementPoints &&
                prev.totalScore === totalScore &&
                prev.multiplier === multiplier
            ) {
                return prev
            }

            return {
                ...prev,
                achievementTitle,
                achievementPoints,
                totalScore,
                multiplier,
            }
        })
    }

    const updatePairState = (frame: ResolvedHudFrame) => {
        const prevPairActive = pairActiveRef.current
        pairActiveRef.current = frame.isPairActive
        pairBonusRef.current = frame.pairBonusScore
        setState(prev => {
            if (
                prev.isPairActive === frame.isPairActive &&
                prev.pairBonusScore === frame.pairBonusScore &&
                prev.pairStreakSec === frame.pairStreakSec
            ) return prev
            return {
                ...prev,
                isPairActive: frame.isPairActive,
                pairBonusScore: frame.pairBonusScore,
                pairStreakSec: frame.pairStreakSec,
            }
        })
    }

    const updateHeaderFromFrame = (frame: ResolvedHudFrame) => {
        const prevMultiplier = multiplierRef.current

        if (frame.totalScore >= totalScoreRef.current) {
            totalScoreRef.current = frame.totalScore
        }

        if (frame.multiplier >= multiplierRef.current) {
            multiplierRef.current = frame.multiplier
        }

        if (frame.achievementTitle.trim().length > 0) {
            achievementTitleRef.current = frame.achievementTitle
        }

        if (frame.achievementPoints > 0) {
            achievementPointsRef.current = frame.achievementPoints
        }

        setHeaderState(
            achievementTitleRef.current,
            achievementPointsRef.current,
            totalScoreRef.current,
            multiplierRef.current,
        )

        if (multiplierRef.current > prevMultiplier) {
            clearTimer(shineTimerRef)

            setState(prev => ({
                ...prev,
                playMultiplierShine: false,
            }))

            shineTimerRef.current = setTimeout(() => {
                setState(prev => ({
                    ...prev,
                    playMultiplierShine: true,
                }))
            }, 250)
        }
    }

    const showFinalScore = (value: number) => {
        clearTimer(finalTimerRef)

        setState(prev => ({
            ...prev,
            isFinalVisible: true,
            finalScore: Math.max(0, Math.round(toSafeNumber(value))),
        }))

        finalTimerRef.current = setTimeout(() => {
            setState(prev => ({
                ...prev,
                isFinalVisible: false,
                finalScore: 0,
            }))
        }, FINALSCOREHIDEDELAY)
    }

    const hideHudWithFooter = (showUnsuccess: boolean) => {
        clearHideTimers()

        if (showUnsuccess) {
            setState(prev => ({
                ...prev,
                scoreToShow: unsuccessedText,
            }))
        } else {
            setState(prev => ({
                ...prev,
                scoreToShow: scoreRef.current,
            }))
        }

        closeTimerRef.current = setTimeout(() => {
            setState(prev => ({
                ...prev,
                footerAnimation: 'drift-score-animation-hide-out',
            }))
        }, FOOTERHIDEDELAY)

        hideTimerRef.current = setTimeout(() => {
            resetHud()
        }, ZEROSCOREHIDEDELAY)
    }

    useEffect(() => {
        return () => {
            clearTimer(closeTimerRef)
            clearTimer(hideTimerRef)
            clearTimer(finalTimerRef)
            clearTimer(shineTimerRef)
        }
    }, [])

    useEffect(() => {
        const resolvedFrame = resolveHudFrame(
            effectiveDriftData,
            previousRawDataRef.current,
        )

        previousRawDataRef.current = effectiveDriftData

        if (!resolvedFrame) {
            return
        }

        if (resolvedFrame.driftEventType === DRIFTEVENTSTART) {
            clearHideTimers()
            clearTimer(finalTimerRef)

            sessionActiveRef.current = true
            scoreRef.current = 0
            totalScoreRef.current = 0
            multiplierRef.current = 1.0
            achievementTitleRef.current = ''
            achievementPointsRef.current = 0

            setState({
                isShown: true,
                score: 0,
                scoreToShow: 0,
                footerAnimation: '',
                isFinalVisible: false,
                finalScore: 0,
                playMultiplierShine: false,
                achievementTitle: '',
                achievementPoints: 0,
                totalScore: 0,
                multiplier: 1.0,
                isPairActive: false,
                pairBonusScore: 0,
                pairStreakSec: 0,
            })

            if (resolvedFrame.totalScore > 0 || resolvedFrame.multiplier > 1) {
                updateHeaderFromFrame(resolvedFrame)
                updatePairState(resolvedFrame)
            }

            return
        }

        if (resolvedFrame.driftEventType === DRIFTEVENTUPDATE) {
            sessionActiveRef.current = true
            showHud()
            setLiveScore(resolvedFrame.driftScore)
            updateHeaderFromFrame(resolvedFrame)
            updatePairState(resolvedFrame)
            return
        }

        if (resolvedFrame.driftEventType === DRIFTEVENTSTOP) {
            if (!sessionActiveRef.current) {
                return
            }

            sessionActiveRef.current = false
            showHud()
            updateHeaderFromFrame(resolvedFrame)
            updatePairState(resolvedFrame)

            const finalLiveScore = Math.max(scoreRef.current, resolvedFrame.driftScore)
            scoreRef.current = finalLiveScore

            setState(prev => ({
                ...prev,
                score: finalLiveScore,
                scoreToShow: finalLiveScore,
            }))

            if (resolvedFrame.totalScore > 0) {
                showFinalScore(resolvedFrame.totalScore)
            }

            const showUnsuccess =
                finalLiveScore <= 0 && resolvedFrame.achievementFinalScore <= 0

            hideHudWithFooter(showUnsuccess)
        }
    }, [effectiveDriftData, unsuccessedText])

    return state
}